import React, {Dispatch} from 'react';
import {useSpring, config, animated} from 'react-spring';
import {CardHeaderBox} from './index.styled';
import {CardTitle} from '../card-title';
import {TestType} from '@esgi/main/libs/core';
import {TestColorIndicator, TestContentArea} from '@esgi/main/kits/common';
import {TestTypeLabel} from '../../../../../../../../kit';
import {TestSession} from '@esgi/main/features/teacher/home';
import {Class, SubjectTab} from '@esgi/main/libs/store';
import {CardActionButtons} from '../card-action-buttons';
import {ClassGroupActionButtonsProps, StudentActionButtonsProps} from '../card-action-buttons/types';

type Props = {
	name: string;
	type: TestType;
	contentArea: TestContentArea;
	lastTestSessions: TestSession[];
	sessionsCount: number;
	maxScore: number;
	dataCy?: string;
	userCanEditTest: boolean;
	openTestDetails: VoidFunction;
	isSmallScreen: boolean;
	testID: number;
	onTestMoveTo: Dispatch<{previousSubject: SubjectTab; newSubject: SubjectTab; testID: number}>;
	onRemoveTest: Dispatch<{subjectID: SubjectTab['id']; testID: number}>;
	classID: Class['id'];
	subjectID: SubjectTab['id'];
} & (StudentActionButtonsProps | ClassGroupActionButtonsProps);

export function CardHeader({
	contentArea,
	name,
	type,
	lastTestSessions,
	sessionsCount,
	maxScore,
	dataCy = 'test-card-header',
	userCanEditTest,
	openTestDetails,
	isSmallScreen,
	testID,
	onTestMoveTo,
	onRemoveTest,
	classID,
	subjectID,
	...props
}: Props) {
	const animatedIndicatorStyles = useSpring({
		height: `calc(100% + ${isSmallScreen ? 0 : 96}px)`,
		marginBottom: isSmallScreen ? 0 : -96,
		config: {
			...config.gentle,
			clamp: true,
		},
	});

	return (
		<CardHeaderBox dataCy={dataCy}>
			<animated.div style={animatedIndicatorStyles}>
				<TestColorIndicator testID={testID} />
			</animated.div>

			<CardTitle titleText={name} dataCy={`${dataCy}-title`} />
			<TestTypeLabel testType={type} dataCy={`${dataCy}-test-type-label`} />
			{!isSmallScreen && (
				<CardActionButtons
					dataCy={dataCy}
					lastTestSessions={lastTestSessions}
					sessionsCount={sessionsCount}
					maxScore={maxScore}
					openTestDetails={openTestDetails}
					userCanEditTest={userCanEditTest}
					testID={testID}
					onTestMoveTo={onTestMoveTo}
					onRemoveTest={onRemoveTest}
					classID={classID}
					subjectID={subjectID}
					{...props}
				/>
			)}
		</CardHeaderBox>
	);
}
